import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./DocumentsAndFiles.module.sass";
import Card from "../../../components/Card";
import Control from "./Control";
import { getStudentDocuments } from "../../../services/documents.service";
import { pb } from "../../../services/pocketbase";
import { updateStudentFileStatus, notificationFolderIncomplete, getStudentById } from "../../../services/student.service";
import { showSuccess, showError } from "../../../services/toast.service";
import ModalImportFile from "../../../components/ModalImportFile";
import Actions from "../../../components/Actions";
import { createStudentMemo } from "../../../services/studentMemo.service";
import { useAuth } from "../../../contexts/AuthContext";


const DocumentsAndFiles = ({ className, studentId, onFileStatusUpdate, currentFileStatus }) => {
  const { currentUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
  
    const handleModalClose = () => {
      setModalVisible(false);
    };

  // Fonction pour récupérer les documents de l'étudiant
  const fetchDocuments = async () => {
    if (!studentId) return;

    setLoading(true);
    try {
      const result = await getStudentDocuments(studentId);
      setDocuments(result.items);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial des documents
  useEffect(() => {
    fetchDocuments();
  }, [studentId]);

  // Fonction pour créer un mémo de relance
  const createRelanceMemo = async () => {
    try {
      const currentDate = new Date().toLocaleDateString('fr-FR');
      const memoData = {
        student_id: studentId,
        topic: `Relance dossier - ${currentDate}`,
        content: JSON.stringify({
          blocks: [{
            key: 'relance',
            text: `Relance effectuée le ${currentDate} pour compléter le dossier étudiant.`,
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {}
          }]
        }),
        created_by: currentUser?.id
      };

      await createStudentMemo(memoData);
      console.log("Mémo de relance créé avec succès");
    } catch (error) {
      console.error("Erreur lors de la création du mémo de relance:", error);
      // Ne pas faire échouer l'action principale si le mémo échoue
    }
  };

  const handleActionSelect = async (selectedAction) => {
    console.log("Action sélectionnée :", selectedAction);
    switch (selectedAction) {
      case "Ajouter un document":
        // Ouvrir la modale des conditions d'utilisation
        setModalVisible(true);
        break;
      case "Dossier complet":
        try {
          // Mettre à jour le statut du dossier comme complet
          await updateStudentFileStatus(studentId, "Complet");
          showSuccess("Le dossier a été marqué comme complet");
          if (onFileStatusUpdate) onFileStatusUpdate();
        } catch (error) {
          console.error("Erreur lors de la mise à jour du statut du dossier:", error);
          showError("Impossible de mettre à jour le statut du dossier");
        }
        break;
      case "Dossier incomplet":
        try {
          // Mettre à jour le statut du dossier comme incomplet
          await updateStudentFileStatus(studentId, "Incomplet");
          showSuccess("Le dossier a été marqué comme incomplet");
          if (onFileStatusUpdate) onFileStatusUpdate();
        } catch (error) {
          console.error("Erreur lors de la mise à jour du statut du dossier:", error);
          showError("Impossible de mettre à jour le statut du dossier");
        }
        break;
      case "Relancer dossier":
        try {
          // Récupérer l'étudiant pour obtenir son user_ref
          const student = await getStudentById(studentId);
          
          if (!student.user_ref) {
            showError("Impossible de trouver la référence utilisateur de l'étudiant");
            return;
          }
          
          // Envoyer la notification de relance
          await notificationFolderIncomplete(student.user_ref);
          
          // Créer un mémo de relance automatiquement
          await createRelanceMemo();
          
          showSuccess("Notification de relance envoyée à l'étudiant et mémo créé");
        } catch (error) {
          console.error("Erreur lors de l'envoi de la notification de relance:", error);
          showError("Impossible d'envoyer la notification de relance");
        }
        break;
      default:
        console.warn("Action non reconnue :", selectedAction);
        break;
    }
  };

  // Définition des actions pour le composant Actions
  const actions = [
    {
      title: "Ajouter un document",
      action: () => handleActionSelect("Ajouter un document")
    },
    // Afficher seulement le bouton opposé au statut actuel
    ...(currentFileStatus === 'Complet' ? [
      {
        title: "Marquer comme incomplet",
        action: () => handleActionSelect("Dossier incomplet")
      }
    ] : [
      {
        title: "Marquer comme complet",
        action: () => handleActionSelect("Dossier complet")
      }
    ]),
    {
      title: "Relancer dossier",
      action: () => handleActionSelect("Relancer dossier")
    }
  ];

  // Fonction pour construire l'URL du fichier
  const getFileUrl = (document) => {
    if (!document || !document.file) return null;
    return `${pb.baseUrl}/api/files/student_documents/${document.id}/${document.file}`;
  };

  // Fonction pour déterminer le type de fichier
  const getFileType = (fileName) => {
    if (!fileName) return 'unknown';
    
    const ext = fileName.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return 'image';
    } else if (ext === 'pdf') {
      return 'pdf';
    } else {
      return 'other';
    }
  };
  
  // Fonction pour retourner l'icône appropriée si prévisu impossible
  const getFileIcon = (fileName) => {
    if (!fileName) return '/images/content/file-document.svg';
    
    const ext = fileName.split('.').pop().toLowerCase();
    
    if (['pdf'].includes(ext)) {
      return '/images/content/file-pdf.svg';
    } else if (['doc', 'docx'].includes(ext)) {
      return '/images/content/file-doc.svg';
    } else if (['xls', 'xlsx'].includes(ext)) {
      return '/images/content/file-xls.svg';
    } else {
      return '/images/content/file-document.svg';
    }
  };
  
  // Fonction pour rendre la prévisualisation selon le type de fichier
  const renderPreview = (document) => {
    const fileUrl = getFileUrl(document);
    if (!fileUrl) {
      return <img src="/images/content/file-document.svg" alt="Document sans fichier" />;
    }
    
    const fileType = getFileType(document.file);
    
    if (fileType === 'image') {
      return (
        <div className={styles.imagePreview}>
          <img src={fileUrl} alt={document.type || 'Image'} />
        </div>
      );
    } else if (fileType === 'pdf') {
      return (
        <div className={styles.pdfPreview}>
          <object
            data={`${fileUrl}#page=1&view=FitH&zoom=50`}
            type="application/pdf"
            width="100%"
            height="100%"
          >
            <img src={getFileIcon(document.file)} alt="PDF document" />
          </object>
        </div>
      );
    } else {
      // Fichiers non prévisualisables
      return <img src={getFileIcon(document.file)} alt={document.type || 'Document'} />;
    }
  };

  // Status badge component to display document status
  const StatusBadge = ({ status }) => {
    // Define status classes and labels using existing status classes
    const statusConfig = {
      ok: { className: "status-green-dark", label: 'ok' },
      ko: { className: "status-red", label: 'ko' },
      pending: { className: "status-yellow", label: 'en attente' },
      submitted: { className: "status-blue", label: 'soumis' }
    };

    const config = statusConfig[status] || { className: "status-gray", label: status };

    return (
      <div className={cn(styles.status, config.className)}>
        {config.label}
      </div>
    );
  };

  // Composant pour afficher un document
  const DocumentItem = ({ document }) => {
    return (
      <div
        key={document.id}
        className={cn(styles.product, {
          [styles.active]: selectedItems.includes(document.id)
        })}
      >
        <div className={styles.preview}>
          
          <Control className={styles.control} document={document} onDocumentChange={fetchDocuments} />
          {renderPreview(document)}

        </div>
        <div className={styles.line}>
          <div className={styles.title}>{document.type || 'Document sans type'}</div>
        </div>
        <div className={styles.dateStatusLine}>
          <div className={styles.date}>{new Date(document.created).toLocaleDateString('fr-FR')}</div>
          <StatusBadge status={document.statut} />
        </div>
      </div>
    );
  };



  return (
    <>
      <Card
        className={cn(styles.card, className)}
        title="Documents élèves"
        classTitle="title-blue"
        head={
          <Actions
            className={styles.actions}
            classActionsHead={styles.actionsHead}
            items={actions}
            small
          />
        }
      >
      
          {loading ? (
            <div className={styles.loading}>Chargement des documents...</div>
          ) : documents.length > 0 ? (
            <div className={styles.products}>
              {documents.map(doc => <DocumentItem key={doc.id} document={doc} />)}
            </div>
          ) : (
            <div className={styles.empty}>
              <p>Aucun document disponible dans cette catégorie.</p>
            </div>
          )}
        
      </Card>
      <ModalImportFile
        visible={modalVisible}
        onClose={handleModalClose}
        studentId={studentId}
        onDocumentUploaded={fetchDocuments} // Rafraîchir la liste après upload
      />
    </>
  );
};

export default DocumentsAndFiles;
